// Tiny two-tone chime synthesized via Web Audio API.
//
// Why not a sound file: zero network cost, zero hosting burden, works
// even when the SPA is loaded from cache, and we sidestep the autoplay
// policy gotchas around <audio src=…> (most browsers block file-based
// audio until first user gesture, but AudioContext is unblocked once
// the page has had any user interaction — which is always true by the
// time a desktop notification fires, because the user logged in).
//
// The chime is deliberately soft: two short sine-wave tones (880Hz →
// 1320Hz, perfect fifth), 80ms each with a gentle attack/decay so it
// reads as "ping" rather than "alarm". Total duration ~200ms. Volume
// peaks at 0.12 — quieter than the OS default notification sound.

export function playChime(): void {
  if (typeof window === 'undefined') return;
  // Safari + older browsers expose AudioContext via webkitAudioContext.
  const AudioCtor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) return;

  let ctx: AudioContext;
  try {
    ctx = new AudioCtor();
  } catch {
    return;
  }

  // Two notes, each a 60ms tone with 20ms attack + decay. Stagger
  // them so the listener hears a "di-da" rather than a single beep.
  const now = ctx.currentTime;
  const peak = 0.12;
  const noteDuration = 0.08;
  const attack = 0.015;
  const release = 0.05;

  function playNote(freq: number, startOffset: number) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const start = now + startOffset;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peak, start + attack);
    gain.gain.linearRampToValueAtTime(0, start + noteDuration + release);
    osc.start(start);
    osc.stop(start + noteDuration + release + 0.02);
  }

  playNote(880, 0);
  playNote(1320, 0.09);

  // Close the context once the chime has finished so we don't leak
  // audio nodes across many fires. setTimeout is fine — the longest
  // any note plays is ~150ms; 400ms is comfortable headroom.
  setTimeout(() => {
    void ctx.close().catch(() => {});
  }, 400);
}
